import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AI_ADVISOR_FAB_GAP_PX, AI_ADVISOR_FAB_SIZE_PX } from '../aiAdvisor/AiAdvisorLauncher';
import { DESKTOP_ADVISOR_FAB_BOTTOM_PX, DASHBOARD_HERO_DESKTOP_MIN_PX } from '../../constants/appShellLayout';
import { isBillUploadEnabled } from '../../constants/uiVisibility';
import { useMsmeProfileQuery } from '../../hooks/queries/useMsmeWorkspaceQueries';
import { continuePathWhileMyCompanyOpen } from '../../utils/myCompanyRequired';

type WorkflowStep = {
  step: string;
  title: string;
  detail: string;
  path: string;
};

const workflowSteps = (): WorkflowStep[] => {
  const bills = isBillUploadEnabled();
  return [
    {
      step: '1',
      title: 'My company',
      detail: 'Confirm the company, then map how the plant uses energy and materials.',
      path: '/msme-profile',
    },
    {
      step: '2',
      title: 'Data',
      detail: bills
        ? 'Upload electricity, fuel, and purchase bills, or connect your accounts.'
        : 'Connect your accounts so activity reaches the footprint.',
      path: bills ? '/document-upload' : '/data?section=connectors',
    },
    {
      step: '3',
      title: 'Carbon footprint',
      detail: 'See Scope 1–3, the hotspots, and the reduction ideas.',
      path: '/carbon-footprint?section=overview',
    },
    {
      step: '4',
      title: 'Compliance',
      detail: 'Prepare one disclosure report: BRSR, ISO 14064, or CBAM.',
      path: '/compliance',
    },
    {
      step: '5',
      title: 'Savings',
      detail: 'kg CO₂e already cut between footprint snapshots.',
      path: '/carbon-savings?section=savings',
    },
    {
      step: '6',
      title: 'Target',
      detail: 'The reduction target for this year and for a longer period.',
      path: '/carbon-savings?section=cut',
    },
    {
      step: '7',
      title: 'Government schemes',
      detail: 'Official government sites. This workspace does not file applications.',
      path: '/carbon-savings?section=schemes',
    },
    {
      step: '8',
      title: 'Offsets and credits',
      detail: 'Offset projects, the credits wallet, prices, and past trades.',
      path: '/carbon-savings?section=trading',
    },
  ];
};

const DashboardWorkflowGuide: React.FC = () => {
  const navigate = useNavigate();
  const profileQuery = useMsmeProfileQuery();
  const profile = profileQuery.data?.data;
  const steps = workflowSteps();
  const open = (path: string) => navigate(continuePathWhileMyCompanyOpen(path, profile));
  const companyStillOpen = continuePathWhileMyCompanyOpen('/document-upload', profile) !== '/document-upload';

  return (
    <Box
      component="section"
      aria-label="How this workspace works"
      sx={{
        mb: 3,
        // The fixed Advisor sits on the viewport's bottom-right. Keep the last
        // step above it, and keep the right column clear while the guide is
        // still two columns (before the lg grid).
        '@media (min-width:900px)': {
          pb: `${DESKTOP_ADVISOR_FAB_BOTTOM_PX + AI_ADVISOR_FAB_SIZE_PX + AI_ADVISOR_FAB_GAP_PX}px`,
        },
        [`@media (min-width:900px) and (max-width:${DASHBOARD_HERO_DESKTOP_MIN_PX - 0.05}px)`]: {
          pr: `${AI_ADVISOR_FAB_SIZE_PX + AI_ADVISOR_FAB_GAP_PX}px`,
        },
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        How this workspace works
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 720 }}>
        {companyStillOpen
          ? 'Finish My company before bills, footprint, and compliance. Those steps stay closed until then.'
          : 'Work through these steps. Each one opens the same page as the left menu.'}
        {' '}
        <Box
          component="button"
          type="button"
          onClick={() => open('/ai-advisor')}
          sx={{
            p: 0,
            border: 0,
            bgcolor: 'transparent',
            color: 'primary.main',
            font: 'inherit',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          AI Advisor
        </Box>
        {' '}
        answers questions about your footprint, water, solar, and how to cut.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
          gap: 1.25,
        }}
      >
        {steps.map((step) => (
          <Box
            key={step.step}
            component="button"
            type="button"
            onClick={() => open(step.path)}
            sx={{
              display: 'flex',
              gap: 1.25,
              textAlign: 'left',
              p: 1.5,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              color: 'text.primary',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <Typography
              variant="subtitle2"
              color="primary"
              sx={{ fontWeight: 800, minWidth: 16, lineHeight: 1.4 }}
            >
              {step.step}
            </Typography>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
                {step.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
                {step.detail}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default DashboardWorkflowGuide;
